import React, {useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {DeclaredChainDTO, DeclaredChainLinkDTO, DeclaredChainsDTO} from '../../@types/safety-recommendation';
import {downloadCsvFile, rowsToCsv} from '../../utils/export/csv';

/** Which record is being shown, which decides the wording as well as the data. */
export type DeclaredChainKind = 'supersessions' | 'reiterations';

export interface DeclaredChainsProps {
    kind: DeclaredChainKind;
    declared: DeclaredChainsDTO | null;
    error?: string | null;
    /** Opens one recommendation in the register, so a chain can be read in full. */
    onOpen?: (id: number) => void;
}

/** The year something happened, or nothing when the source never dated it. */
const yearOf = (date: string | null | undefined): string => (date ? date.slice(0, 4) : '');

/**
 * How current this is.
 *
 * <p>A view whose whole claim is that it shows the issuer's own record has to say when it last read
 * it, or the claim quietly expires.</p>
 */
const syncedAt = (lastSyncedAt: string | null | undefined): string => (lastSyncedAt
    ? `Last taken from the NTSB on ${new Date(lastSyncedAt).toLocaleDateString()}.`
    : 'The local copy has never been synced with the NTSB.');

/** One row per link, carrying its chain's details, which is what a spreadsheet wants. */
export const toCsv = (kind: DeclaredChainKind, chains: DeclaredChainDTO[]): string => rowsToCsv(
    kind === 'supersessions'
        ? ['Supersedes', 'Issued', 'Status', 'Superseded', 'Its issue date', 'Its status', 'Superseded on']
        : ['Recommendation', 'Issued', 'Status', 'Reiterated by case'],
    chains.flatMap((chain) => (chain.links.length === 0
        ? [[chain.subject.number, chain.subject.issuedDate ?? '', chain.subject.statusGroup, '', '', '', '']]
        : chain.links.map((link) => (kind === 'supersessions'
            ? [
                chain.subject.number,
                chain.subject.issuedDate ?? '',
                chain.subject.statusGroup,
                link.recommendation?.number ?? link.reference ?? '',
                link.recommendation?.issuedDate ?? '',
                link.recommendation?.statusGroup ?? '',
                link.date ?? '',
            ]
            : [
                chain.subject.number,
                chain.subject.issuedDate ?? '',
                chain.subject.statusGroup,
                link.reference ?? '',
            ])))),
);

/**
 * What the issuer itself recorded as coming back to a recommendation.
 *
 * <p>The register's repeated asks are found by resemblance, which is the point of them: the issuer
 * marked far fewer connections than its recommendations actually repeat. These are the marked ones,
 * and they are worth seeing on their own, both because they are the issuer's own account and because
 * they are the ground truth the resemblance grouping was measured against.</p>
 */
export const DeclaredChains: React.FC<DeclaredChainsProps> = ({kind, declared, error, onOpen}) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const superseding = kind === 'supersessions';
    const chains = declared?.chains ?? null;

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (chains === null || declared === null) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading</Typography>
            </Stack>
        );
    }
    if (chains.length === 0) {
        return <Typography variant="body2">The issuer has recorded none of these.</Typography>;
    }

    const download = (): void => downloadCsvFile(
        `ntsb-${kind}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(kind, chains));

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{flex: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {(superseding
                            ? `${chains.length} NTSB recommendations that replaced an earlier one, `
                              + `out of the ${declared.corpusTotal.toLocaleString()} held here, `
                            : `${chains.length} of the ${declared.corpusTotal.toLocaleString()} NTSB `
                              + 'recommendations held here, ')
                            + `carrying ${declared.linkCount.toLocaleString()} `
                            + (superseding ? 'supersessions' : 'reiterations')
                            + (declared.linkCount > chains.length
                                ? (superseding
                                    ? ', because some replaced more than one.'
                                    : ', because the NTSB came back to some of them more than once.')
                                : '.')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {superseding
                            ? 'Each recommendation the NTSB published carries the keys of the recommendations '
                              + 'that replaced it, and those keys are what this is built from, turned around: the '
                              + 'recommendation that stands now is named first, then what it replaced, with the '
                              + 'date the NTSB recorded.'
                            : 'Each recommendation the NTSB published carries the investigations tied to it, and '
                              + 'those marked as reiterating are what this is built from. The recommendation is '
                              + 'named first, then the investigations whose reports repeated it.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {'Nothing here is inferred: these are the NTSB\'s own links, taken from the source by the '
                            + 'sync, unlike repeated asks, which are grouped by resemblance and find far more. '
                            + syncedAt(declared.lastSyncedAt)}
                    </Typography>
                </Box>
                <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={download}>
                    CSV
                </Button>
            </Stack>

            {chains.map((chain: DeclaredChainDTO) => {
                const key = String(chain.subject.id);
                const open = expanded[key] ?? false;
                return (
                    <Paper key={key} variant="outlined" sx={{p: 1.25}}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Link
                                        component="button"
                                        variant="body2"
                                        fontWeight={600}
                                        onClick={(): void => onOpen?.(chain.subject.id)}
                                    >
                                        {chain.subject.number}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary">
                                        {yearOf(chain.firstIssued)}
                                    </Typography>
                                    <Chip size="small" label={chain.subject.statusGroup.replace(/_/g, ' ')} />
                                    <Typography variant="caption" color="text.secondary">
                                        {superseding
                                            ? `supersedes ${chain.links.length} `
                                              + `recommendation${chain.links.length === 1 ? '' : 's'}`
                                              + (chain.spanYears > 0 ? `, going back ${chain.spanYears} years` : '')
                                            : `repeated in ${chain.links.length} later `
                                              + `investigation${chain.links.length === 1 ? '' : 's'}`}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Button
                                size="small"
                                endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={(): void => setExpanded({...expanded, [key]: !open})}
                            >
                                {open ? 'Hide' : (superseding ? 'Superseded' : 'Investigations')}
                            </Button>
                        </Stack>

                        <Collapse in={open}>
                            <Stack spacing={0.5} sx={{mt: 1, pl: 1}}>
                                {chain.links.map((link: DeclaredChainLinkDTO, index: number) => (
                                    <Stack
                                        key={`${key}-${link.recommendation?.id ?? link.reference ?? index}`}
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        flexWrap="wrap"
                                    >
                                        {link.recommendation ? (
                                            <>
                                                <Link
                                                    component="button"
                                                    variant="body2"
                                                    onClick={(): void => onOpen?.(link.recommendation!.id)}
                                                >
                                                    {link.recommendation.number}
                                                </Link>
                                                <Typography variant="caption" color="text.secondary">
                                                    {yearOf(link.recommendation.issuedDate)}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={link.recommendation.statusGroup.replace(/_/g, ' ')}
                                                />
                                            </>
                                        ) : (
                                            <Typography variant="body2">{link.reference}</Typography>
                                        )}
                                        {link.date && (
                                            <Typography variant="caption" color="text.secondary">
                                                {`on ${link.date}`}
                                            </Typography>
                                        )}
                                        {!link.recommendation && superseding && (
                                            <Typography variant="caption" color="text.secondary">
                                                not held in the local copy
                                            </Typography>
                                        )}
                                    </Stack>
                                ))}
                                {chain.links.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        The issuer recorded none.
                                    </Typography>
                                )}
                            </Stack>
                        </Collapse>
                    </Paper>
                );
            })}
        </Stack>
    );
};

export default DeclaredChains;
